import React from "react";
import { HeartHandshake, Clock, FileText, Users } from "lucide-react";
import { Reveal, SectionLabel } from "./primitives";

const points = [
  { icon: Clock, title: "More time", body: "Extra time to talk things through and make decisions at your own pace." },
  { icon: FileText, title: "Different formats", body: "Information in a way that works for you, or communication by your preferred method." },
  { icon: Users, title: "A nominated person", body: "Someone you trust can speak with us on your behalf, with your permission." },
];

export const VulnerableSupport = () => (
  <section
    id="extra-support"
    aria-labelledby="extra-support-heading"
    className="border-t border-brand-border bg-brand-bg py-16 lg:py-24"
  >
    <div className="mx-auto max-w-content px-4 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
        <Reveal>
          <SectionLabel>Extra support</SectionLabel>
          <h2
            id="extra-support-heading"
            className="mt-3 font-heading text-3xl font-bold tracking-tight text-brand-navy lg:text-4xl"
          >
            Here for you when life is harder
          </h2>
          <div className="mt-5 space-y-4 text-lg leading-relaxed text-brand-body">
            <p>
              We know that life can be difficult, and that some people need a little extra help.
              Whether you’re dealing with ill health, a bereavement, a mental health condition, a
              disability, caring responsibilities or a sudden change in circumstances, we’re here
              to listen — without judgement.
            </p>
            <p>
              If there’s anything that would make things easier for you, please tell us. We’ll do
              our best to adjust how we work with you so you get the support that’s right for you.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="flex items-start gap-4 rounded-2xl border border-brand-teal/30 bg-white p-6">
            <span className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl bg-brand-teal/10 text-brand-teal">
              <HeartHandshake className="h-6 w-6" aria-hidden="true" />
            </span>
            <p className="text-base leading-relaxed text-brand-body">
              <span className="font-heading font-bold text-brand-navy">Just let us know.</span>{" "}
              A quiet word is all it takes — we’ll note your preferences and make sure you’re
              looked after every time you get in touch.
            </p>
          </div>

          <ul className="mt-4 grid gap-3 sm:grid-cols-3">
            {points.map((p) => {
              const Icon = p.icon;
              return (
                <li
                  key={p.title}
                  className="rounded-2xl border border-brand-border bg-white p-5"
                  data-testid={`vulnerable-point-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <Icon className="h-6 w-6 text-brand-teal" aria-hidden="true" />
                  <p className="mt-3 font-heading text-base font-bold text-brand-navy">{p.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-brand-body">{p.body}</p>
                </li>
              );
            })}
          </ul>
        </Reveal>
      </div>
    </div>
  </section>
);
